import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, homeFor } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MapPin, ShieldCheck, Workflow, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const doLogin = async (e, presetEmail) => {
    if (e) e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(presetEmail || email, presetEmail ? "password" : password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
      navigate(homeFor(user.role), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset email");
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="bg-slate-900 text-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <div className="flex items-center gap-2.5 relative z-10">
          <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center"><MapPin size={20} className="text-white" /></div>
          <span className="font-display font-bold text-xl tracking-tight">PinPoint</span>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-blue-400 mb-4">Property Operating System</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">Property.<br />Perfectly Managed.</h1>
          <p className="text-slate-400 mt-5 text-base leading-relaxed">One connected platform for staff, landlords, tenants and contractors — everyone sees exactly their part.</p>
          <div className="mt-10 space-y-4">
            {[[Building2, "Every property, one connected workspace"], [Workflow, "Workflow-driven automation"], [ShieldCheck, "Role-based access — secure by design"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Icon size={16} className="text-blue-400" /></div>{t}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-500 relative z-10">© 2026 PinPoint. Built for the UK property industry.</p>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          {mode === "forgot" ? (
            <>
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Forgot password</h2>
              <p className="text-slate-500 text-sm mt-2">Enter your email and we'll send you a link to reset your password.</p>
              {forgotSent ? (
                <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="forgot-sent">
                  <p className="text-sm font-medium text-emerald-800">Check your inbox</p>
                  <p className="text-sm text-emerald-700 mt-1">If an account exists for <b>{forgotEmail}</b>, a password reset link is on its way. The link expires in 1 hour.</p>
                  <button data-testid="forgot-back" onClick={() => { setMode("login"); setForgotSent(false); }} className="mt-4 text-sm font-medium text-blue-700 hover:text-blue-800">Back to sign in</button>
                </div>
              ) : (
                <form onSubmit={doForgot} className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input data-testid="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required placeholder="you@agency.com"
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                  <button data-testid="forgot-submit" type="submit" disabled={forgotBusy}
                    className="w-full bg-slate-900 text-white rounded-lg px-4 py-2.5 font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {forgotBusy ? <Loader2 size={16} className="animate-spin" /> : null} Send reset link
                  </button>
                  <button type="button" data-testid="forgot-cancel" onClick={() => setMode("login")} className="w-full text-sm font-medium text-slate-500 hover:text-slate-700">Back to sign in</button>
                </form>
              )}
            </>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Sign in</h2>
              <p className="text-slate-500 text-sm mt-2">Access your PinPoint workspace.</p>

              <form onSubmit={doLogin} className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@agency.com"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-slate-700">Password</label>
                    <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotEmail(email); setMode("forgot"); }} className="text-xs font-medium text-blue-700 hover:text-blue-800">Forgot password?</button>
                  </div>
                  <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
                <button data-testid="login-submit" type="submit" disabled={busy}
                  className="w-full bg-slate-900 text-white rounded-lg px-4 py-2.5 font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null} Sign in
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
